import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosHeaderValue,
} from 'axios';
import axiosRetry from 'axios-retry';
import router from '@/router';
import pinia from '@/store';
import useUserStore from '@/store/user';
import * as Sentry from '@sentry/vue';
// import { sendReport } from './report';
import { getStorage, STORAGE_KEYS } from './storage';
// import { Toast } from './toast';

// 定义请求配置类型
interface RequestConfig extends AxiosRequestConfig {
  skipCodeCheck?: boolean; // 是否跳过错误处理
}

// Helper function to get brand/model based on platform (matches VUE_APP_API_IDENTIFIER, e.g. WebOS, Tizen, Vidaa)
export const getModel = () => {
  const platform = (process.env.VUE_APP_API_IDENTIFIER || '').toLowerCase();
  if (platform === 'tizen') return 'Samsung';
  if (platform === 'webos') return 'LG';
  if (platform === 'vidaa') return 'Hisense';
  return 'Unknown';
};

// Helper to get the current Jaco user id for headers
// Priority: explicit "id" field, then "firebase_id", otherwise empty string
export const getUserIdForHeaders = () => {
  try {
    const userInfo = getStorage<any>(STORAGE_KEYS.USER_INFO);
    return userInfo?.id || userInfo?.firebase_id || '';
  } catch (error) {
    console.warn('[Axios] Failed to read user id from storage for headers:', error);
    return '';
  }
};

const service: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL, // api base_url
  timeout: 10 * 1000, // Reduced timeout for better TV responsiveness
  withCredentials: false,
  headers: {
    // Core required headers (will also be reinforced in the interceptor)
    v_p: '1.12.0',
    OS: process.env.VUE_APP_API_IDENTIFIER,
    model: getModel(),
    brand: getModel(),
    UA: navigator.userAgent,
    uid: getUserIdForHeaders(),
    language: getStorage(STORAGE_KEYS.LOCALE) === 'en' ? 0 : 1,
  },
});

// Configure retry logic with exponential backoff
axiosRetry(service, {
  retries: 3, // Retry failed requests 3 times
  retryDelay: (retryCount: number, error: any) => {
    console.log(`[Axios] Retry ${retryCount} for ${error.config?.url}`);
    // Exponential backoff: 1s, 2s, 4s
    return Math.min(1000 * Math.pow(2, retryCount - 1), 4000);
  },
  retryCondition: (error: any) => {
    // Don't retry if the request was explicitly cancelled
    if (error.code === 'ERR_CANCELED' || error.message?.includes('abort')) {
      console.log('[Axios] Request was cancelled, not retrying');
      return false;
    }

    // Don't retry SSO endpoints as aggressively (they have their own timeout handling)
    const isSSOEndpoint = error.config?.url?.includes('/sso/');
    if (isSSOEndpoint && error.code === 'ECONNABORTED') {
      console.log('[Axios] SSO timeout, not retrying');
      return false;
    }

    // Retry on network errors or 5xx errors
    const shouldRetry =
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response?.status !== undefined && error.response.status >= 500 && error.response.status < 600);

    if (shouldRetry) {
      console.log(`[Axios] Will retry: ${error.message}`);
    }

    return shouldRetry;
  },
  onRetry: (retryCount: number, error: any, requestConfig: any) => {
    console.log(`[Axios] Retrying request (${retryCount}/3): ${requestConfig.url}`);
  },
});

// request interceptor
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig<AxiosRequestConfig>) => {
    // Ensure required headers are always present on every request that goes through this instance.
    // Use the AxiosHeaders helper to avoid type issues with direct object mutation.
    const headers = axios.AxiosHeaders.from(config.headers || {});

    // Normalize and ensure core headers; do not override if caller explicitly set them.
    if (headers.get('v_p') === undefined) {
      headers.set('v_p', '1.12.0' as any);
    }
    if (headers.get('os') === undefined) {
      headers.set('os', process.env.VUE_APP_API_IDENTIFIER as any);
    }
    if (headers.get('model') === undefined) {
      headers.set('model', getModel() as any);
    }
    if (headers.get('ua') === undefined) {
      headers.set('ua', navigator.userAgent as any);
    }

    // Always try to attach latest uid from storage (if available)
    const uid = getUserIdForHeaders();
    if (uid) {
      headers.set('uid', uid as any);
    }

    config.headers = headers;

    // Remove v_p header for BFF homepage API
    if (config.url?.includes('/bff/homepage/tv/c/get.json')) {
      //delete config.headers['v_p'];
    }

    // 在发送请求之前做些什么
    // 比如，可以在这里添加 token
    // const vgsid = createStorage(STORAGE_KEYS.VGSID, '');
    // if (vgsid) {
    //   config.headers['vgsid'] = vgsid;
    // }
    // if (store.getters.token) {
    //   config.headers['X-Token'] = getToken();
    // }
    return config;
  },
  error => {
    // 对请求错误做些什么
    console.log(error);
    return Promise.reject(error);
  },
);

// response interceptor
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data;
    const config = response.config as RequestConfig;
    // if (res.error_code !== 10000) {
    //   Toast(
    //     '登录已过期，请重新登录' + res.error_code + JSON.stringify(res?.config) + JSON.stringify(res?.data),
    //     20 * 1000,
    //   );
    // }
    if ([35000, 35100, 35200, 35300].includes(res.error_code)) {
      try {
        const userStore = useUserStore(pinia);
        userStore.logout();
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: 'Logout from interceptor' },
        });
      }
      setTimeout(() => {
        router.replace({ name: 'QRLogin' });
      }, 100);
      return Promise.reject(new Error(res.error || 'Error'));
    }
    // 如果自定义代码不是10000，则判断为错误。
    if (res.error_code !== 10000 && !config.skipCodeCheck) {
      return Promise.reject(new Error(res.error || 'Error'));
    }
    return res;
  },
  error => {
    console.log(`err${error}`);
    console.log(error?.response?.status);
    return Promise.reject(error);
  },
);
/**
 * 设置请求头
 * @param key
 * @param val
 */
export function setAxiosHeader(key: string, val: AxiosHeaderValue) {
  service.defaults.headers[key] = val;
}
export function getAxiosHeader(key: string) {
  return service.defaults.headers[key];
}

/**
 * 设置baseURL
 * @param baseUrl
 * @returns
 */
export function setAxiosBaseURL(baseUrl: string) {
  service.defaults.baseURL = baseUrl;
}

/**
 * 获取当前baseURL
 * @returns
 */
export function getAxiosBaseURL(): string {
  return service.defaults.baseURL || '';
}

// 泛型请求函数// 通用请求函数
function request<T>(config: RequestConfig): Promise<T> {
  // const requestStartTime = Date.now();
  // 请求前埋点
  try {
    // sendReport({
    //   event: 'logs-tv_interface_elk_start',
    //   reportType: 'delay',
    //   extend: {
    //     baseUrl: process.env.VUE_APP_REPORT_BASE_URL,
    //     interfacePath: config.url,
    //     interfaceTime: requestStartTime,
    //     interfaceParams: JSON.stringify(config?.params || config?.data),
    //   },
    // });
  } catch (error) {
    console.warn('请求开始埋点失败:', error);
  }

  return service
    .request<any, T>(config)
    .then((response: T) => {
      // const responseTime = Date.now();
      // const duration = responseTime - requestStartTime;
      // 请求成功埋点
      try {
        // sendReport({
        //   event: 'logs-tv_interface_elk_success',
        //   reportType: 'delay',
        //   extend: {
        //     baseUrl: process.env.VUE_APP_REPORT_BASE_URL,
        //     interfacePath: config.url,
        //     interfaceTime: duration,
        //     interfaceCode: JSON.stringify((response as any)?.error_code),
        //     interfaceData: JSON.stringify((response as any)?.data),
        //   },
        // });
      } catch (error) {
        console.warn('请求成功埋点失败:', error);
      }
      return response;
    })
    .catch(error => {
      // const responseTime = Date.now();
      // const duration = responseTime - requestStartTime;
      // 请求失败埋点
      try {
        // sendReport({
        //   event: 'logs-tv_interface_elk_catch',
        //   reportType: 'delay',
        //   extend: {
        //     baseUrl: process.env.VUE_APP_REPORT_BASE_URL,
        //     catchErrorPath: config.url,
        //     catchErrorInfo: JSON.stringify(error),
        //     interfaceTime: duration,
        //   },
        // });
      } catch (reportError) {
        console.warn('请求失败埋点失败:', reportError);
      }
      throw error;
    });
}

// 便捷方法
request.get = <T = any>(url: string, config?: RequestConfig): Promise<T> => {
  return request<T>({ ...config, method: 'GET', url });
};

request.post = <T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> => {
  return request<T>({ ...config, method: 'POST', url, data });
};

export { request, RequestConfig, service as axiosInstance };
export default request;
